/* eslint-disable react/no-array-index-key */
import React from "react";
import {
  Grid,
  TextField,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Chip,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  CardHeader,
  Tooltip,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Schema as SchemaIcon,
  DataObject as DataIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import MarketSelector from "../primitives/MarketSelector";

const SchemaGenerator = ({
  selectedSchemaMarket,
  onSchemaMarketSelect,
  newSchemaCode,
  onNewSchemaCodeChange,
  onAddSchemaMarket,
  ajvSchemas,
  filePreview,
  selectedAttribute,
  onAttributeSelect,
  onPropertyChange,
  ajvFormatOptions,
  enumInputValue,
  onEnumInputValueChange,
  onEnumInputKeyPress,
  onRemoveEnumValue,
  onToggleRequired,
  onEnumInputBlur,
}) => {
  const getTypeChipColor = (type) => {
    const colors = {
      string: "primary",
      number: "secondary",
      integer: "info",
      boolean: "success",
      array: "warning",
      object: "error",
      null: "default",
    };
    return colors[type] || "default";
  };

  const getFormatDisplay = (format) => {
    const formatLabels = {
      email: "Email",
      date: "Date",
      "date-time": "DateTime",
      url: "URL",
      uuid: "UUID",
      ipv4: "IPv4",
      ipv6: "IPv6",
    };
    return formatLabels[format] || format;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
        Schema Generator
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
        Define JSON Schema validation rules for different markets. Configure data types, formats,
        constraints, and validation rules for each field in your data.
      </Typography>

      <Card elevation={2} sx={{ mb: 3 }}>
        <CardHeader
          avatar={<SchemaIcon color="primary" />}
          title="Country Schema Selection"
          subheader="Choose or create a country-specific schema configuration"
          slotProps={{
            title: { variant: "h6", fontWeight: 600 },
          }}
          action={
            <Tooltip title="Schemas define validation rules for different countries based on ISO codes and local regulations">
              <IconButton>
                <InfoIcon />
              </IconButton>
            </Tooltip>
          }
        />
        <CardContent>
          <MarketSelector
            title="Country Schema Selection"
            selectedMarket={selectedSchemaMarket}
            onMarketSelect={onSchemaMarketSelect}
            markets={ajvSchemas}
            newMarketCode={newSchemaCode}
            onNewMarketCodeChange={(e) => onNewSchemaCodeChange(e.target.value.toUpperCase())}
            onAddMarket={onAddSchemaMarket}
            newMarketInputLabel="New Country Code (ISO)"
            newMarketInputPlaceholder="e.g., US, GB, DE, FR, AU"
            newMarketInputHelper="ISO country code (will be automatically uppercase)"
          />
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Left Panel - Source Attributes */}
        <Grid size={4}>
          <Card elevation={1} sx={{ height: 600 }}>
            <CardHeader
              avatar={<DataIcon color="secondary" />}
              title="Source Data Fields"
              subheader={`${Object.keys(filePreview || {}).length} fields detected`}
              slotProps={{
                title: { variant: "h6", fontWeight: 600 },
              }}
            />
            <Divider />
            <CardContent sx={{ p: 0, height: "calc(100% - 80px)", overflow: "auto" }}>
              <List disablePadding>
                {Object.keys(filePreview || {}).map((attr) => {
                  const isSelected = attr === selectedAttribute;
                  // const currentType =
                  //   ajvSchemas[selectedSchemaMarket]?.properties?.[attr]?.type || "undefined";
                  const currentType = typeof filePreview[attr];
                  const sampleValue = filePreview[attr];

                  return (
                    <ListItem key={attr} disablePadding>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => onAttributeSelect(attr)}
                        sx={{
                          py: 2,
                          px: 3,
                          borderLeft: isSelected ? "4px solid" : "4px solid transparent",
                          borderLeftColor: isSelected ? "primary.main" : "transparent",
                          "&.Mui-selected": {
                            backgroundColor: "primary.50",
                          },
                        }}
                      >
                        <ListItemText
                          sx={{ flex: 1, minWidth: 0 }}
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body1"
                                fontWeight={isSelected ? 600 : 400}
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  flex: 1,
                                  minWidth: 0,
                                  color: isSelected ? "primary.main" : "text.primary",
                                  fontSize: isSelected ? "1rem" : "0.875rem",
                                }}
                              >
                                {attr}
                              </Typography>
                              <Chip
                                label={currentType}
                                size="small"
                                color={getTypeChipColor(currentType)}
                                variant="outlined"
                                sx={{ flexShrink: 0 }}
                              />
                            </Box>
                          }
                          secondary={
                            sampleValue !== null &&
                            sampleValue !== undefined &&
                            sampleValue !== "" ? (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{
                                  display: "block",
                                  mt: 0.5,
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "300px",
                                }}
                                title={
                                  typeof sampleValue === "object"
                                    ? JSON.stringify(sampleValue, null, 2)
                                    : String(sampleValue)
                                }
                              >
                                Sample: &ldquo;
                                {typeof sampleValue === "object"
                                  ? JSON.stringify(sampleValue)
                                  : String(sampleValue)}
                                &rdquo;
                              </Typography>
                            ) : null
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Panel - Schema Configuration */}
        <Grid size={8}>
          <Card elevation={1} sx={{ height: 600 }}>
            <CardHeader
              avatar={<SettingsIcon color="primary" />}
              title={
                selectedAttribute ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Configure Field:{" "}
                    <Chip label={selectedAttribute} color="primary" variant="filled" />
                  </Box>
                ) : (
                  "Field Configuration"
                )
              }
              subheader={
                selectedAttribute
                  ? "Define validation rules and constraints for this field"
                  : "Select a field from the left panel to configure its schema"
              }
              slotProps={{
                title: { variant: "h6", fontWeight: 600 },
              }}
            />
            <Divider />
            <CardContent sx={{ height: "calc(100% - 120px)", overflow: "auto", p: 2 }}>
              {selectedAttribute ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Required Field */}
                  <Paper variant="outlined" sx={{ p: 3, backgroundColor: "grey.50" }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Validation Requirements
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={ajvSchemas[selectedSchemaMarket].required.includes(
                            selectedAttribute
                          )}
                          onChange={() => onToggleRequired(selectedAttribute)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Required Field
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            This field must be present and cannot be empty
                          </Typography>
                        </Box>
                      }
                    />
                  </Paper>

                  {/* Type Configuration */}
                  <Paper variant="outlined" sx={{ p: 3, backgroundColor: "grey.50" }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Data Type
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      label="Field Type"
                      value={
                        ajvSchemas[selectedSchemaMarket].properties[selectedAttribute]?.type || ""
                      }
                      onChange={(e) => onPropertyChange("type", e.target.value)}
                      helperText="Choose the expected data type for validation"
                    >
                      {["string", "number", "integer", "boolean", "array", "object", "null"].map(
                        (opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        )
                      )}
                    </TextField>
                  </Paper>

                  {/* Format Configuration */}
                  <Paper variant="outlined" sx={{ p: 3, backgroundColor: "grey.50" }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Format & Pattern
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <TextField
                          select
                          fullWidth
                          label="Format"
                          value={
                            ajvSchemas[selectedSchemaMarket].properties[selectedAttribute]
                              ?.format || ""
                          }
                          onChange={(e) => onPropertyChange("format", e.target.value)}
                          helperText="Predefined format validation"
                        >
                          <MenuItem value="">
                            <em>No specific format</em>
                          </MenuItem>
                          {ajvFormatOptions.map((fmt) => (
                            <MenuItem key={fmt} value={fmt}>
                              {getFormatDisplay(fmt)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid size={6}>
                        <TextField
                          fullWidth
                          label="Pattern (RegEx)"
                          placeholder="^[a-zA-Z0-9]+$"
                          value={
                            ajvSchemas[selectedSchemaMarket].properties[selectedAttribute]
                              ?.pattern || ""
                          }
                          onChange={(e) => onPropertyChange("pattern", e.target.value)}
                          helperText="Custom regex pattern validation"
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Length Constraints */}
                  <Paper variant="outlined" sx={{ p: 3, backgroundColor: "grey.50" }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Length Constraints
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <TextField
                          fullWidth
                          label="Minimum Length"
                          type="number"
                          inputProps={{ min: 0 }}
                          value={
                            ajvSchemas[selectedSchemaMarket].properties[selectedAttribute]
                              ?.minLength || ""
                          }
                          onChange={(e) => onPropertyChange("minLength", Number(e.target.value))}
                          helperText="Minimum character count"
                        />
                      </Grid>
                      <Grid size={6}>
                        <TextField
                          fullWidth
                          label="Maximum Length"
                          type="number"
                          inputProps={{ min: 0 }}
                          value={
                            ajvSchemas[selectedSchemaMarket].properties[selectedAttribute]
                              ?.maxLength || ""
                          }
                          onChange={(e) => onPropertyChange("maxLength", Number(e.target.value))}
                          helperText="Maximum character count"
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Enum Values */}
                  <Paper variant="outlined" sx={{ p: 3, backgroundColor: "grey.50" }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Allowed Values (Enum)
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      label="Add allowed value"
                      placeholder="Type a value and press Enter to add"
                      value={enumInputValue}
                      onChange={(e) => onEnumInputValueChange(e.target.value)}
                      onKeyPress={onEnumInputKeyPress}
                      onBlur={onEnumInputBlur}
                      helperText="Type a specific value and press Enter to add it to the allowed values list"
                      FormHelperTextProps={{
                        sx: { color: "error.main", fontWeight: 500 },
                      }}
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {(
                        ajvSchemas[selectedSchemaMarket].properties[selectedAttribute]?.enum || []
                      ).map((value, index) => (
                        <Chip
                          key={`${value}-${index}`}
                          label={value}
                          onDelete={() => onRemoveEnumValue(value)}
                          size="medium"
                          color="primary"
                          variant="outlined"
                          sx={{ m: 0.25 }}
                        />
                      ))}
                    </Box>
                    {(ajvSchemas[selectedSchemaMarket].properties[selectedAttribute]?.enum || [])
                      .length === 0 && (
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        No specific values defined - field accepts any valid value of the specified
                        type
                      </Typography>
                    )}
                  </Paper>
                </Box>
              ) : (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "text.secondary",
                  }}
                >
                  <SchemaIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" gutterBottom>
                    Select a Field to Configure
                  </Typography>
                  <Typography variant="body2">
                    Choose a field from the left panel to define its validation schema, data type,
                    format constraints, and other validation rules.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SchemaGenerator;
